import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { UploadCloud, Loader2, FileText, AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { parseCsvFile } from '@/lib/csvParser';
import { mapCsvToTrades } from './dataMapper';
import { ExternalStrategyData, TradeData } from './types';

interface ExternalStrategyUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStrategyAdd: (strategyData: ExternalStrategyData) => void;
}

export const ExternalStrategyUploadModal: React.FC<ExternalStrategyUploadModalProps> = ({
  open,
  onOpenChange,
  onStrategyAdd,
}) => {
  const [strategyName, setStrategyName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const resetState = () => {
    setStrategyName('');
    setDescription('');
    setSelectedFile(null);
    setIsProcessing(false);
    setError(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Basic type check
      if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
        setSelectedFile(file);
        setError(null); // Clear previous errors
      } else if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf') || file.type.startsWith('image/')) {
         setSelectedFile(file);
         setError("PDF/Image analysis requires backend processing (Coming Soon). Uploaded file noted.");
         toast({ title: "File Type Notice", description: "PDF/Image analysis is not yet processed client-side.", variant: "info"});
      }
      else {
        setError('Unsupported file type. Please upload a CSV file.');
        setSelectedFile(null);
      }
    }
  };

  const handleUploadAndProcess = useCallback(async () => {
    if (!selectedFile || !strategyName.trim()) {
      setError('Strategy name and a CSV file are required.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    const fileType = selectedFile.name.toLowerCase().endsWith('.csv') ? 'csv' :
                     selectedFile.name.toLowerCase().endsWith('.pdf') ? 'pdf' : 'png';

    if (fileType === 'pdf' || fileType === 'png') {
        const externalData: ExternalStrategyData = {
            id: `ext_${Date.now()}`,
            name: strategyName.trim(),
            description: description.trim(),
            source: 'external',
            fileType: fileType,
            status: 'pending_analysis',
            trades: [], // No trades parsed client-side
            rawFileData: selectedFile, // Optionally store file ref if needed later
            ytdReturn: undefined, // No return can be calculated yet
        };
        onStrategyAdd(externalData);
        toast({ title: "Upload Successful", description: `Strategy '${strategyName.trim()}' added. Analysis for ${fileType.toUpperCase()} pending.`});
        onOpenChange(false);
        resetState();
        setIsProcessing(false);
        return;
    }


    // Process CSV
    try {
      const csvData = await parseCsvFile(selectedFile);
      const trades = mapCsvToTrades(csvData);

      if (trades.length === 0) {
          throw new Error("No valid trades could be extracted from the CSV. Please check the file format and ensure required columns (Date, Symbol, Action, Quantity, Price) are present.");
      }


      // Calculate a simple YTD return for sorting
      // Find the earliest and latest trades to calculate the return
      const sortedTrades = [...trades].sort((a, b) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      
      let ytdReturn: number | undefined = undefined;
      
      // Only calculate if we have at least two trades
      if (sortedTrades.length >= 2) {
        const firstTrade = sortedTrades[0];
        const lastTrade = sortedTrades[sortedTrades.length - 1];
        
        // Simple profit calculation (can be refined based on actual trade data)
        const buySum = sortedTrades.filter(t => t.action === 'buy')
          .reduce((sum, t) => sum + (t.price * t.quantity), 0);
          
        const sellSum = sortedTrades.filter(t => t.action === 'sell')
          .reduce((sum, t) => sum + (t.price * t.quantity), 0);
          
        // Calculate return if we have both buys and sells
        if (buySum > 0) {
          ytdReturn = ((sellSum - buySum) / buySum) * 100;
        }
      }
      
      const externalData: ExternalStrategyData = {
        id: `ext_${Date.now()}`,
        name: strategyName.trim(),
        description: description.trim(),
        source: 'external',
        fileType: 'csv',
        status: 'active',
        trades: trades,
        ytdReturn: ytdReturn, // Add calculated return for sorting
      };

      onStrategyAdd(externalData);
      toast({
        title: "Import Successful",
        description: `Strategy '${externalData.name}' imported with ${trades.length} trades.`,
        variant: "success"
      });
      onOpenChange(false); // Close modal on success
      resetState(); // Reset form

    } catch (err: any) {
      console.error("Error processing CSV:", err);
      setError(err.message || 'Failed to process CSV file.');
       toast({
        title: "Import Failed",
        description: err.message || 'Could not process the CSV file. Please check the format.',
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  }, [selectedFile, strategyName, description, onStrategyAdd, onOpenChange, toast]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { onOpenChange(isOpen); if (!isOpen) resetState(); }}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UploadCloud className="h-5 w-5" />
            Import External Strategy Data
          </DialogTitle>
          <DialogDescription>
            Upload a CSV file containing your strategy's trade history. PDF/Image support coming soon.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          {error && (
             <Alert variant={error.includes('Unsupported') ? 'destructive' : 'warning'}>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
             </Alert>
          )}
          <div className="space-y-2">
            <Label htmlFor="strategy-name">Strategy Name *</Label>
            <Input
              id="strategy-name"
              value={strategyName}
              onChange={(e) => setStrategyName(e.target.value)}
              placeholder="e.g., My Options Strategy"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="strategy-description">Description (Optional)</Label>
            <Input
              id="strategy-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the strategy"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="file-upload">Trade History File (CSV Preferred) *</Label>
            <div className="flex items-center gap-2">
                 <Input
                  id="file-upload"
                  type="file"
                  accept=".csv,application/pdf,image/png,image/jpeg" // Accept multiple types
                  onChange={handleFileChange}
                  className="flex-1"
                />
                {selectedFile && <FileText className="h-5 w-5 text-gray-500"/>}
            </div>
            <p className="text-xs text-gray-500">
              CSV Format: Ensure columns like Date, Symbol, Action, Quantity, Price are present.
            </p>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isProcessing}>Cancel</Button>
          </DialogClose>
          <Button
            onClick={handleUploadAndProcess}
            disabled={isProcessing || !selectedFile || !strategyName.trim()}
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...
              </>
            ) : (
              "Upload and Process"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
